import React, { useState, useCallback } from 'react';
import { X, Plus, Trash2, Upload } from 'lucide-react';
import * as XLSX from 'xlsx';
import { createClient, createSlab } from '../../api/createClientApi';
import { createRoute } from '../../api/routesApi';
import { toast } from '../shared/Toast';

const FormInput = ({ label, placeholder, width = '100%', value, onChange, maxLength }) => (
  <div style={{ display: 'flex', flexDirection: 'column', position: 'relative', width }}>
    <label style={{
      position: 'absolute', top: '-8px', left: '12px', backgroundColor: 'white',
      padding: '0 4px', fontSize: '11px', color: '#666', fontWeight: 500, zIndex: 1
    }}>
      {label}
    </label>
    <input
      type="text"
      placeholder={placeholder}
      value={value}
      onChange={onChange}
      maxLength={maxLength}
      style={{
        padding: '12px 14px', border: '1px solid #D1D5DB', borderRadius: '4px',
        fontSize: '14px', color: '#333', outline: 'none', backgroundColor: 'transparent',
        width: '100%', boxSizing: 'border-box'
      }}
    />
  </div>
);

const RateInput = ({ label, placeholder, value, onChange }) => (
  <div style={{ display: 'flex', flexDirection: 'column', position: 'relative', flex: 1, minWidth: '110px', flexShrink: 1 }}>
    <label style={{
      position: 'absolute', top: '-8px', left: '12px', backgroundColor: 'white',
      padding: '0 4px', fontSize: '11px', color: '#666', fontWeight: 500, zIndex: 1,
      whiteSpace: 'nowrap'
    }}>
      {label}
    </label>
    <div style={{ display: 'flex', alignItems: 'center', border: '1px solid #D1D5DB', borderRadius: '4px', padding: '0 12px' }}>
      <input
        type="text"
        placeholder={placeholder}
        value={value}
        onChange={onChange}
        style={{ padding: '12px 0', border: 'none', fontSize: '14px', color: '#333', outline: 'none', backgroundColor: 'transparent', flex: 1, minWidth: 0 }}
      />
      <span style={{ fontSize: '12px', color: '#666', paddingLeft: '8px' }}>Rs/-</span>
    </div>
  </div>
);

const SectionTitle = ({ children }) => (
  <h3 style={{ fontSize: '15px', fontWeight: 700, color: '#333', margin: '32px 0 16px' }}>
    {children}
  </h3>
);

const AddClientModal = ({ isOpen, onClose, onSave }) => {
  const [slabs, setSlabs] = useState([{ id: 1, from: '', to: '' }]);
  const [rateCards, setRateCards] = useState([{ sedan: '', suv: '', escortSedan: '', escortSuv: '' }]);
  const [routes, setRoutes] = useState([{ id: 1, routeName: '', fromLocation: '', toLocation: '', distanceKm: '' }]);
  const [file, setFile] = useState(null);
  const [isDragging, setIsDragging] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [uploadMode, setUploadMode] = useState(false);
  const [clientData, setClientData] = useState({
    name: '',
    location: '',
    contactPerson: '',
    contactNumber: '',
    email: ''
  });

  // Define callbacks before early return
  const handleDragOver = useCallback((e) => {
    e.preventDefault();
    setIsDragging(true);
  }, []);

  const handleDragLeave = useCallback((e) => {
    e.preventDefault();
    setIsDragging(false);
  }, []);

  const handleDrop = useCallback((e) => {
    e.preventDefault();
    setIsDragging(false);
    const droppedFile = e.dataTransfer.files[0];
    if (droppedFile && (droppedFile.name.endsWith('.xlsx') || droppedFile.name.endsWith('.xls'))) {
      setFile(droppedFile);
    }
  }, []);

  if (!isOpen) return null;

  const handleFileSelect = (e) => {
    const selectedFile = e.target.files[0];
    if (selectedFile) {
      setFile(selectedFile);
    }
  };

  const parseExcelFile = (file) => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = (e) => {
        try {
          const data = new Uint8Array(e.target.result);
          const workbook = XLSX.read(data, { type: 'array' });
          const firstSheet = workbook.Sheets[workbook.SheetNames[0]];
          const jsonData = XLSX.utils.sheet_to_json(firstSheet);
          resolve(jsonData);
        } catch (error) {
          reject(error);
        }
      };
      reader.onerror = (error) => reject(error);
      reader.readAsArrayBuffer(file);
    });
  };

  const handleSubmit = async () => {
    // Excel upload mode
    if (uploadMode) {
      if (!file) {
        toast.warning('Please select an Excel file');
        return;
      }
      try {
        setUploading(true);
        
        // Parse Excel file on frontend
        const excelData = await parseExcelFile(file);
        
        if (!excelData || excelData.length === 0) {
          toast.warning('No data found in Excel file');
          setUploading(false);
          return;
        }

        const results = [];
        const errors = [];

        // Process each row - create client, slabs, and routes
        for (const row of excelData) {
          try {
            // Extract client data
            const clientPayload = {
              clientName: row.name || row.clientName || '',
              contactPerson: row.contactPerson || '',
              contactNumber: row.contactNumber || '',
              email: row.email || '',
              address: row.location || row.address || ''
            };

            if (!clientPayload.clientName) {
              errors.push(`Skipping row: Missing client name`);
              continue;
            }

            // Create client
            const clientResult = await createClient(clientPayload);
            
            if (clientResult.success) {
              const clientId = clientResult.data?.id || clientResult.data?.clientId;
              
              if (clientId) {
                // Create slab if slab data exists
                const hasSlabData = row.startRange || row.endRange || row.sedanRate || row.suvRate || row.escortSedanRate || row.escortSuvRate || row.escortRate;
                if (hasSlabData) {
                  await createSlab({
                    clientId: clientId,
                    slabType: row.slabType || 'Slab',
                    startRange: row.startRange?.toString() || '',
                    endRange: row.endRange?.toString() || '',
                    sedanRate: row.sedanRate?.toString() || '',
                    suvRate: row.suvRate?.toString() || '',
                    escortSedanRate: (row.escortSedanRate || row.escortForSedanRate || row.escortRate)?.toString() || '',
                    escortSuvRate: (row.escortSuvRate || row.escortForSuvRate || row.escortRate)?.toString() || ''
                  });
                }

                // Create route if route data exists
                // Handle various column name formats
                const routeNameVal = row.routeName || row.routename || row.RouteName || '';
                const fromLocationVal = row.fromLocation || row.fromlocation || row.FromLocation || row.pickupLocation || row.pickuplocation || row.PickupLocation || row.pickup || row.From || row.from || '';
                const toLocationVal = row.toLocation || row.tolocation || row.ToLocation || row.dropLocation || row.droplocation || row.DropLocation || row.drop || row.To || row.to || '';
                const distanceKmVal = row.distanceKm || row.distancekm || row.DistanceKm || row.distance || row.Distance || row.routeDistance || row.routedistance || '';
                
                if (routeNameVal || fromLocationVal || toLocationVal || distanceKmVal) {
                  await createRoute({
                    clientId: clientId,
                    routeName: routeNameVal,
                    fromLocation: fromLocationVal,
                    toLocation: toLocationVal,
                    distanceKm: distanceKmVal?.toString() || ''
                  });
                }
              }
              
              results.push(clientResult);
            } else {
              errors.push(`Failed to create client ${clientPayload.clientName}: ${clientResult.message}`);
            }
          } catch (rowError) {
            errors.push(`Error processing row: ${rowError.message}`);
          }
        }

        onClose();
        setTimeout(() => {
          if (errors.length > 0) {
            toast.error(`Upload completed with ${results.length} successes and ${errors.length} errors.`);
          } else {
            toast.success(`Successfully uploaded ${results.length} client(s) with slabs and routes!`);
          }
          onSave?.({ success: true, data: results });
        }, 500);
        
      } catch (err) {
        const errorMsg = err.response?.data?.message || err.message || 'Error uploading file';
        toast.error(errorMsg);
      } finally {
        setUploading(false);
      }
      return;
    }

    // Manual entry mode
    if (!clientData.name.trim()) {
      toast.warning('Please enter client name');
      return;
    }
    if (!clientData.location.trim()) {
      toast.warning('Please enter location/address');
      return;
    }
    if (!clientData.contactPerson.trim()) {
      toast.warning('Please enter contact person');
      return;
    }
    if (!clientData.contactNumber.trim()) {
      toast.warning('Please enter contact number');
      return;
    }
    const phoneRegex = /^\d{10}$/;
    if (!phoneRegex.test(clientData.contactNumber.trim())) {
      toast.warning('Please enter a valid 10-digit contact number (numbers only)');
      return;
    }
    if (!clientData.email.trim()) {
      toast.warning('Please enter email address');
      return;
    }
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(clientData.email)) {
      toast.warning('Please enter a valid email address');
      return;
    }

    // Validate slabs and rate cards
    for (let i = 0; i < slabs.length; i++) {
      const slab = slabs[i];
      const rc = rateCards[i];
      
      if (!slab.from.trim() || !slab.to.trim()) {
        toast.warning(`Please fill all start and end ranges for Slab #${i + 1}`);
        return;
      }
      const fromVal = parseFloat(slab.from);
      const toVal = parseFloat(slab.to);
      if (isNaN(fromVal) || fromVal < 0 || isNaN(toVal) || toVal <= 0) {
        toast.warning(`Slab #${i + 1} range values must be positive numbers`);
        return;
      }
      if (toVal <= fromVal) {
        toast.warning(`Slab #${i + 1} end range must be greater than start range`);
        return;
      }

      // Validate rates
      if (!rc || !rc.sedan.trim() || !rc.suv.trim() || !rc.escortSedan.trim() || !rc.escortSuv.trim()) {
        toast.warning(`Please enter rates (Sedan, SUV, Sedan Escort, SUV Escort) for Slab #${i + 1}`);
        return;
      }
      const sedanRate = parseFloat(rc.sedan);
      const suvRate = parseFloat(rc.suv);
      const escortSedanRate = parseFloat(rc.escortSedan);
      const escortSuvRate = parseFloat(rc.escortSuv);
      if (
        isNaN(sedanRate) || sedanRate < 0 ||
        isNaN(suvRate) || suvRate < 0 ||
        isNaN(escortSedanRate) || escortSedanRate < 0 ||
        isNaN(escortSuvRate) || escortSuvRate < 0
      ) {
        toast.warning(`All rate values in Slab #${i + 1} must be valid non-negative numbers`);
        return;
      }
    }

    // Validate routes
    for (let i = 0; i < routes.length; i++) {
      const route = routes[i];
      // If any route field is filled, enforce all fields for that route
      const hasAnyField = route.routeName.trim() || route.fromLocation.trim() || route.toLocation.trim() || route.distanceKm.trim();
      if (hasAnyField) {
        if (!route.routeName.trim() || !route.fromLocation.trim() || !route.toLocation.trim() || !route.distanceKm.trim()) {
          toast.warning(`Please fill all fields (Route Name, From, To, Distance) for Route #${i + 1}`);
          return;
        }
        const distVal = parseFloat(route.distanceKm);
        if (isNaN(distVal) || distVal <= 0) {
          toast.warning(`Distance for Route #${i + 1} must be a valid positive number`);
          return;
        }
      }
    }
    try {
      setUploading(true);
      const payload = {
        clientName: clientData.name,
        contactPerson: clientData.contactPerson,
        contactNumber: clientData.contactNumber,
        address: clientData.location,
        email: clientData.email || '',
        slabs: slabs.map((s, i) => ({
          from: s.from,
          to: s.to,
          sedan: rateCards[i]?.sedan,
          suv: rateCards[i]?.suv,
          escortSedan: rateCards[i]?.escortSedan,
          escortSuv: rateCards[i]?.escortSuv,
          sedanRate: rateCards[i]?.sedan,
          suvRate: rateCards[i]?.suv,
          escortSedanRate: rateCards[i]?.escortSedan,
          escortSuvRate: rateCards[i]?.escortSuv,
          escortForSedanRate: rateCards[i]?.escortSedan,
          escortForSuvRate: rateCards[i]?.escortSuv
        })),
        routes: routes.filter(r => r.routeName.trim() || r.fromLocation.trim() || r.toLocation.trim() || r.distanceKm.trim())
      };
      const result = await createClient(payload);
      if (result.success) {
        toast.success('Client created successfully!');
        onSave && onSave(result);
        onClose();
      } else {
        toast.error(result.message || 'Failed to create client');
      }
    } catch (err) {
      const errorMsg = err.response?.data?.message || err.message || 'Error creating client';
      toast.error(errorMsg);
    } finally {
      setUploading(false);
    }
  };

  const addSlab = () => {
    setSlabs(prev => [...prev, { id: Date.now(), from: '', to: '' }]);
    setRateCards(prev => [...prev, { sedan: '', suv: '', escortSedan: '', escortSuv: '' }]);
  };

  const removeSlab = (id) => {
    const index = slabs.findIndex(s => s.id === id);
    if (slabs.length > 1) {
      setSlabs(prev => prev.filter(s => s.id !== id));
      setRateCards(prev => prev.filter((_, i) => i !== index));
    }
  };

  const updateSlab = (id, field, value) => {
    setSlabs(prev => prev.map(s => s.id === id ? { ...s, [field]: value } : s));
  };

  const updateRateCard = (index, field, value) => {
    setRateCards(prev => prev.map((rc, i) => i === index ? { ...rc, [field]: value } : rc));
  };

  const addRoute = () => {
    setRoutes(prev => [...prev, { id: Date.now(), routeName: '', fromLocation: '', toLocation: '', distanceKm: '' }]);
  };

  const removeRoute = (id) => {
    if (routes.length > 1) {
      setRoutes(prev => prev.filter(r => r.id !== id));
    }
  };

  const updateRoute = (id, field, value) => {
    setRoutes(prev => prev.map(r => r.id === id ? { ...r, [field]: value } : r));
  };

  return (
    <>
      <div onClick={onClose} style={{ position: 'fixed', inset: 0, backgroundColor: 'rgba(0,0,0,0.5)', zIndex: 2000 }} />
      <div style={{
        position: 'fixed', top: '50%', left: '50%', transform: 'translate(-50%,-50%)',
        backgroundColor: 'white', zIndex: 2001, width: '90%', maxWidth: '900px',
        boxShadow: '0 24px 48px rgba(0,0,0,0.2)', maxHeight: '90vh', display: 'flex', flexDirection: 'column',
        borderRadius: '8px', overflow: 'hidden'
      }}>

        {/* Header */}
        <div style={{ backgroundColor: '#227B48', padding: '16px 32px', display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexShrink: 0 }}>
          <h2 style={{ margin: 0, fontSize: '20px', fontWeight: 700, color: 'white' }}>Add New Client</h2>
          <button onClick={onClose} style={{
            background: 'none', border: 'none', cursor: 'pointer', color: '#F04438',
            display: 'flex', alignItems: 'center', gap: '6px', fontSize: '14px', fontWeight: 500
          }}>
            Cancel <X size={18} style={{ backgroundColor: '#F04438', color: 'white', borderRadius: '50%', padding: '2px' }} />
          </button>
        </div>

        {/* Content */}
        <div style={{ padding: '32px', overflowY: 'auto', overflowX: 'hidden', flex: 1 }}>

          {/* Mode Toggle */}
          <div style={{ display: 'flex', gap: '12px', marginBottom: '24px' }}>
            <button
              onClick={() => setUploadMode(false)}
              style={{
                flex: 1,
                padding: '12px',
                borderRadius: '6px',
                border: uploadMode ? '1px solid #D1D5DB' : '2px solid #20B967',
                backgroundColor: uploadMode ? 'white' : '#EEF8F3',
                color: uploadMode ? '#666' : '#20B967',
                fontSize: '14px',
                fontWeight: 600,
                cursor: 'pointer'
              }}
            >
              Manual Entry
            </button>
            <button
              onClick={() => setUploadMode(true)}
              style={{
                flex: 1,
                padding: '12px',
                borderRadius: '6px',
                border: uploadMode ? '2px solid #20B967' : '1px solid #D1D5DB',
                backgroundColor: uploadMode ? '#EEF8F3' : 'white',
                color: uploadMode ? '#20B967' : '#666',
                fontSize: '14px',
                fontWeight: 600,
                cursor: 'pointer'
              }}
            >
              Upload Excel
            </button>
          </div>

          {/* Drop Zone - only in upload mode */}
          {uploadMode && (
          <div 
            onDragOver={handleDragOver}
            onDragLeave={handleDragLeave}
            onDrop={handleDrop}
            onClick={() => document.getElementById('fileInput').click()}
            style={{
              border: `1px dashed ${isDragging ? '#20B967' : '#A0A0A0'}`, 
              borderRadius: '8px', 
              padding: '120px 32px',
              textAlign: 'center', 
              marginBottom: '24px',
              backgroundColor: isDragging ? '#EEF8F3' : 'white',
              cursor: 'pointer',
              transition: 'all 0.2s',
              minHeight: '300px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center'
            }}
          >
            <input
              type="file"
              id="fileInput"
              accept=".xlsx,.xls"
              onChange={handleFileSelect}
              style={{ display: 'none' }}
            />
            {file ? (
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px' }}>
                <Upload size={24} color="#20B967" />
                <span style={{ fontSize: '16px', color: '#333', fontWeight: 500 }}>{file.name}</span>
              </div>
            ) : (
              <span style={{ fontSize: '24px', color: '#666', fontWeight: 600 }}>Drop XL Sheet or Click to Upload</span>
            )}
          </div>
          )}

          {/* Client Details, Slabs, Rate Card - only in manual entry mode */}
          {!uploadMode && (
            <>
          <SectionTitle>Client Details</SectionTitle>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr 1fr 1fr', gap: '16px' }}>
            <FormInput 
              label="Name" 
              placeholder="Enter client name" 
              value={clientData.name}
              onChange={(e) => setClientData({...clientData, name: e.target.value})}
            />
            <FormInput 
              label="Location" 
              placeholder="Enter Location"
              value={clientData.location}
              onChange={(e) => setClientData({...clientData, location: e.target.value})}
            />
            <FormInput 
              label="Contact Person" 
              placeholder="Contact Person Name"
              value={clientData.contactPerson}
              onChange={(e) => setClientData({...clientData, contactPerson: e.target.value})}
            />
            <FormInput 
              label="Contact Number" 
              placeholder="Contact Number"
              value={clientData.contactNumber}
              maxLength={10}
              onChange={(e) => setClientData({...clientData, contactNumber: e.target.value.replace(/\D/g, '').slice(0, 10)})}
            />
            <FormInput 
              label="Email" 
              placeholder="Email Address"
              value={clientData.email}
              onChange={(e) => setClientData({...clientData, email: e.target.value})}
            />
          </div>

          <SectionTitle>Add Slabs</SectionTitle>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '24px' }}>
            {slabs.map((slab, index) => (
              <div key={slab.id} style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                {/* Slabs / Fixed radio per row */}
                <div style={{ display: 'flex', gap: '24px', alignItems: 'center' }}>
                  <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer', fontSize: '16px', color: '#333' }}>
                    <input type="radio" name={`slabType-${slab.id}`} defaultChecked style={{ accentColor: '#333', width: '20px', height: '20px', cursor: 'pointer' }} /> Slabs
                  </label>
                  <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer', fontSize: '16px', color: '#333' }}>
                    <input type="radio" name={`slabType-${slab.id}`} style={{ accentColor: '#333', width: '20px', height: '20px', cursor: 'pointer' }} /> Fixed
                  </label>
                </div>
                {/* From / To / Action Button */}
                <div style={{ display: 'flex', gap: '16px', alignItems: 'center' }}>
                  <FormInput label="From" placeholder="Start Range" width="220px" value={slab.from} onChange={(e) => updateSlab(slab.id, 'from', e.target.value)} />
                  <FormInput label="To" placeholder="End Range" width="220px" value={slab.to} onChange={(e) => updateSlab(slab.id, 'to', e.target.value)} />
                  {index === slabs.length - 1 ? (
                    <button onClick={addSlab} style={{
                      backgroundColor: '#20B967', color: 'white', border: 'none', borderRadius: '6px',
                      padding: '12px 20px', fontSize: '15px', fontWeight: 600, display: 'flex', alignItems: 'center',
                      gap: '6px', cursor: 'pointer', height: '46px', whiteSpace: 'nowrap', flexShrink: 0
                    }}>
                      Add New Slab <Plus size={18} />
                    </button>
                  ) : (
                    <button onClick={() => removeSlab(slab.id)} style={{
                      backgroundColor: '#F04438', color: 'white', border: 'none', borderRadius: '6px',
                      padding: '12px', cursor: 'pointer', height: '46px', width: '46px', display: 'flex',
                      alignItems: 'center', justifyContent: 'center', flexShrink: 0
                    }}>
                      <Trash2 size={18} />
                    </button>
                  )}
                </div>
              </div>
            ))}
          </div>

          <SectionTitle>Rate Card</SectionTitle>
          {slabs.map((slab, index) => (
            <div key={slab.id} style={{ display: 'flex', gap: '16px', alignItems: 'center', marginBottom: '16px' }}>
              <div style={{ backgroundColor: '#F9FAFB', padding: '12px 16px', borderRadius: '4px', fontSize: '13px', color: '#6B7280', fontWeight: 500, height: '46px', display: 'flex', alignItems: 'center', whiteSpace: 'nowrap', minWidth: '120px', justifyContent: 'center' }}>
                {slab.from || '0'}km to {slab.to || '0'}km
              </div>
              <RateInput label="Sedan" value={rateCards[index]?.sedan || ''} onChange={(e) => updateRateCard(index, 'sedan', e.target.value)} />
              <RateInput label="SUV" value={rateCards[index]?.suv || ''} onChange={(e) => updateRateCard(index, 'suv', e.target.value)} />
              <RateInput label="Sedan Escort" value={rateCards[index]?.escortSedan || ''} onChange={(e) => updateRateCard(index, 'escortSedan', e.target.value)} />
              <RateInput label="SUV Escort" value={rateCards[index]?.escortSuv || ''} onChange={(e) => updateRateCard(index, 'escortSuv', e.target.value)} />
            </div>
          ))}

          <SectionTitle>Routes</SectionTitle>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
            {routes.map((route, index) => (
              <div key={route.id} style={{ display: 'flex', gap: '16px', alignItems: 'center' }}>
                <FormInput
                  label="routeName"
                  placeholder="Enter route name"
                  width="180px"
                  value={route.routeName}
                  onChange={(e) => updateRoute(route.id, 'routeName', e.target.value)}
                />
                <FormInput
                  label="fromLocation"
                  placeholder="Enter pickup location"
                  width="200px"
                  value={route.fromLocation}
                  onChange={(e) => updateRoute(route.id, 'fromLocation', e.target.value)}
                />
                <FormInput
                  label="toLocation"
                  placeholder="Enter drop location"
                  width="200px"
                  value={route.toLocation}
                  onChange={(e) => updateRoute(route.id, 'toLocation', e.target.value)}
                />
                <FormInput
                  label="distanceKm"
                  placeholder="Enter distance"
                  width="140px"
                  value={route.distanceKm}
                  onChange={(e) => updateRoute(route.id, 'distanceKm', e.target.value)}
                />
                {index === routes.length - 1 ? (
                  <button onClick={addRoute} style={{
                    backgroundColor: '#20B967', color: 'white', border: 'none', borderRadius: '6px',
                    padding: '12px 16px', fontSize: '14px', fontWeight: 600, display: 'flex', alignItems: 'center',
                    gap: '4px', cursor: 'pointer', height: '46px', whiteSpace: 'nowrap', flexShrink: 0
                  }}>
                    Add Route <Plus size={16} />
                  </button>
                ) : (
                  <button onClick={() => removeRoute(route.id)} style={{
                    backgroundColor: '#F04438', color: 'white', border: 'none', borderRadius: '6px',
                    padding: '12px', cursor: 'pointer', height: '46px', width: '46px', display: 'flex',
                    alignItems: 'center', justifyContent: 'center', flexShrink: 0
                  }}>
                    <Trash2 size={18} />
                  </button>
                )}
              </div>
            ))}
          </div>
            </>
          )}

        </div>

        {/* Footer */}
        <div style={{ padding: '16px 32px 24px 32px', display: 'flex', justifyContent: 'flex-end', flexShrink: 0 }}>
          <button 
            onClick={handleSubmit} 
            disabled={uploading}
            style={{
              backgroundColor: uploading ? '#9CA3AF' : '#20B967', 
              color: 'white', 
              border: 'none', 
              borderRadius: '6px',
              padding: '14px 28px', 
              fontSize: '15px', 
              fontWeight: 600, 
              cursor: uploading ? 'not-allowed' : 'pointer'
            }}
          >
            {uploading ? 'Uploading...' : (uploadMode ? 'Submit' : 'Create & Save')}
          </button>
        </div>

      </div>
    </>
  );
};

export default AddClientModal;
